import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_API_URL;

const ScoutLogin: React.FC = () => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const apiUrl = BACKEND_URL || 'http://localhost:5000/api';
      const response = await fetch(`${apiUrl}/scout/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok) {
        localStorage.setItem('token', data.token);
        localStorage.setItem('userRole', 'scout');
        localStorage.setItem('userName', data.scout.name);
        localStorage.setItem('userEmail', data.scout.email);
        // Save user object for AuthContext compatibility
        const userObj = {
          id: data.scout.id,
          name: data.scout.name,
          email: data.scout.email,
          type: 'scout',
          isSubscriber: false
        };
        localStorage.setItem('user', JSON.stringify(userObj));
        
        // Force reload to trigger AuthContext to pick up the new user
        window.location.href = '/scout/dashboard';
      } else {
        setError(data.error || t('auth.loginFailed'));
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(t('auth.networkError'));
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-900 via-emerald-800 to-teal-900 flex items-center justify-center px-4">
      <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl p-8 w-full max-w-md border border-white/20">
        <div className="text-center mb-8">
          <img 
            src="/BowlerProLogo1.png" 
            alt="BowlerPro Logo" 
            className="mx-auto max-h-40 w-auto"
          />
          <h2 className="text-2xl font-bold text-white mb-2">
            {t('auth.scoutLogin')}
          </h2>
          <p className="text-emerald-200">
            {t('auth.scoutLoginSubtitle')}
          </p>
        </div>

        {error && (
          <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg">
            <p className="text-white text-sm text-center">{error}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="email" className="block text-emerald-100 font-medium mb-2">
              {t('auth.email')}
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
              placeholder={t('auth.emailPlaceholder')}
            />
          </div>

          <div>
            <label htmlFor="password" className="block text-emerald-100 font-medium mb-2">
              {t('auth.password')}
            </label>
            <input
              type="password"
              id="password"
              name="password"
              value={formData.password}
              onChange={handleChange}
              required
              className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-emerald-300 focus:outline-none focus:ring-2 focus:ring-emerald-400 focus:border-transparent"
              placeholder={t('auth.passwordPlaceholder')}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white font-bold py-3 px-6 rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? t('auth.loggingIn') : t('auth.login')}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-emerald-200">
            {t('auth.dontHaveAccount')}{' '}
            <Link to="/signup/scout" className="text-emerald-300 hover:text-emerald-100 font-semibold transition">
              {t('auth.signUp')}
            </Link>
          </p>
        </div>

        <div className="mt-4 text-center">
          <Link to="/" className="text-emerald-300 hover:text-emerald-100 text-sm transition">
            ← {t('auth.backToHome')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ScoutLogin;
